import { useState, useEffect } from "react";
import "../../assets/css/FullSignup/FullSignup.css";
import emailjs from "emailjs-com";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import {
  BASE_URL,
  EMAIL_SERVICE_ID,
  EMAIL_TEMPLATE_ID,
  EMAIL_PUBLIC_KEY,
} from "../../store/config";
import ErrorModal from "../common/ErrorModal"; // ✅ ADDED
 
function FullSignup() {
  const [formData, setFormData] = useState({
    email: "",
    countryCode: "+91",
    contact: "",
    password: "",
    confirmPassword: "",
  });
 
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false); // ✅ ADDED
  const [errorMessage, setErrorMessage] = useState("");
  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
 
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem("isLoggedIn") === "true"
  );
 
  const navigate = useNavigate();
 
  const validatePassword = (password) => {
  if (password.length < 8)
    return "At least 8 characters required";
 
  if (!/[A-Z]/.test(password))
    return "Password must contain one uppercase letter";
 
  if (!/[a-z]/.test(password))
    return "Password must contain one lowercase letter";
 
  if (!/[0-9]/.test(password))
    return "Password must contain one number";
 
  if (!/[!@#$%^&*]/.test(password))
    return "Password must contain one special character";
 
  return null;
};
 
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/welcome");
    }
  }, [isAuthenticated, navigate]);
 
  // ✅ ADDED (helper function only)
  const showError = (message) => {
    setError(message);
    setShowErrorModal(true);
  };
 
  const handleChange = (e) => {
    const { name, value } = e.target;
 
    if (name === "email") {
      setOtpSent(false);
      setOtp("");
      setOtpVerified(false);
      sessionStorage.removeItem("otpHash");
      sessionStorage.removeItem("otpExpiry");
    }
    if (name === "contact") {
      setFormData({ ...formData, contact: value.replace(/\D/g, "") });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
 
  // Generate 6-digit OTP
  const generateOtp = () =>
    Math.floor(100000 + Math.random() * 900000).toString();
 
  // Hash OTP using SHA-256
  const hashOtp = async (otp) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(otp);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };
 
  const sendOtpEmail = async (otp) => {
    return emailjs.send(
      EMAIL_SERVICE_ID,
      EMAIL_TEMPLATE_ID,
      {
        email: formData.email,
        passcode: otp,
      },
      EMAIL_PUBLIC_KEY,
    );
  };
 
  const handleSendOtp = async () => {
    if (!formData.email) {
      showError("Please enter an email first.");
      return;
    }
 
    if (!isValidEmail(formData.email)) {
      showError("Please enter a valid email address.");
      return;
    }
    try {
      const otpGenerated = generateOtp();
      const hashedOtp = await hashOtp(otpGenerated);
 
      sessionStorage.setItem("otpHash", hashedOtp);
      sessionStorage.setItem("otpExpiry", Date.now() + 2 * 60 * 1000);
 
      await sendOtpEmail(otpGenerated);
 
      setOtpSent(true);
      setError("");
    } catch (err) {
      console.error(err);
      showError("Failed to send OTP. Try again.");
    }
  };
 
  const handleVerifyOtp = async () => {
    if (!otp) {
      showError("Please enter OTP!");
      return;
    }
 
    const storedHash = sessionStorage.getItem("otpHash");
    const expiry = sessionStorage.getItem("otpExpiry");
 
    if (!storedHash || Date.now() > expiry) {
      showError("OTP expired");
      return;
    }
 
    const enteredHash = await hashOtp(otp);
 
    if (enteredHash === storedHash) {
      sessionStorage.removeItem("otpHash");
      sessionStorage.removeItem("otpExpiry");
 
      setOtpVerified(true);
      setError("");
      alert("Email verified successfully!"); // optional feedback
    } else {
      showError("Invalid OTP");
      setOtpVerified(false);
    }
  };
 
  const handleSubmit = async (e) => {
    if (!e.target.checkValidity()) {
      return;
    }
 
    e.preventDefault();
 
    if (!otpVerified) {
      showError("Please verify your email OTP first.");
      return;
    }
 
    if (!/^\d{10}$/.test(formData.contact)) {
      showError("Contact number must be exactly 10 digits");
      return;
    }
 
    // Validate password strength
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      showError(passwordError);
      return;
    }
 
    // Validate confirm password strength too
    const confirmPasswordError = validatePassword(formData.confirmPassword);
    if (confirmPasswordError) {
      showError("Confirm Password: " + confirmPasswordError);
      return;
    }
 
    if (formData.password !== formData.confirmPassword) {
      showError("Passwords do not match");
      return;
    }
 
    if (!formData.email) {
      showError("Email is required");
      return;
    }
 
    setError("");
 
    try {
      const response = await axios.post(`${BASE_URL}api/auth/signup`, {
        email: formData.email,
        contact_no: formData.contact,
        country_code: formData.countryCode,
        password: formData.password,
      });
 
      const { access_token, is_first_user } = response.data;
 
      // Store token & login state
      sessionStorage.setItem("access_token", access_token);
      sessionStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("first_time_user", is_first_user ? "True" : "False");
      setIsAuthenticated(true);
    } catch (err) {
      console.error(err);
 
      if (err.response && err.response.status === 409) {
        showError("Email already registered");
      } else {
        showError("Failed to create account. Try again.");
      }
    }
  };
 
  if (isAuthenticated) {
    return <Navigate to="/welcome" replace />;
  }
 
  const handleReEnterEmail = () => {
    setOtpSent(false);
    setOtp("");
    setOtpVerified(false);
    setError("");
 
    sessionStorage.removeItem("otpHash");
    sessionStorage.removeItem("otpExpiry");
  };
 
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
 
  return (
    <div className="full-card">
      <h2 className="full-title">Create Account</h2>
 
      <form onSubmit={handleSubmit}>
        {/* Email + Send OTP */}
        <div className="full-field full-email-otp">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <button
            type="button"
            className="full-btn-otp"
            onClick={handleSendOtp}
          >
            Send OTP
          </button>
        </div>
 
        {/* OTP Input + Verify button */}
        {otpSent && (
          <div className="full-field full-otp-verify">
            <input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              maxLength={6}
            />
            <button
              type="button"
              className="full-btn-otp"
              onClick={handleVerifyOtp}
            >
              Verify OTP
            </button>
          </div>
        )}
 
        {/* Country code + contact */}
        <div className="full-field full-contact">
          <select
            name="countryCode"
            value={formData.countryCode}
            onChange={handleChange}
            className="country-code"
          >
            <option value="+93">+93 AFG</option>
            <option value="+355">+355 ALB</option>
            <option value="+213">+213 DZA</option>
            <option value="+1-684">+1-684 ASM</option>
            <option value="+376">+376 AND</option>
            <option value="+244">+244 AGO</option>
            <option value="+1-264">+1-264 AIA</option>
            <option value="+1-268">+1-268 ATG</option>
            <option value="+54">+54 ARG</option>
            <option value="+374">+374 ARM</option>
            <option value="+297">+297 ABW</option>
            <option value="+61">+61 AUS</option>
            <option value="+43">+43 AUT</option>
            <option value="+994">+994 AZE</option>
            <option value="+1-242">+1-242 BHS</option>
            <option value="+973">+973 BHR</option>
            <option value="+880">+880 BGD</option>
            <option value="+1-246">+1-246 BRB</option>
            <option value="+375">+375 BLR</option>
            <option value="+32">+32 BEL</option>
            <option value="+501">+501 BLZ</option>
            <option value="+229">+229 BEN</option>
            <option value="+1-441">+1-441 BMU</option>
            <option value="+975">+975 BTN</option>
            <option value="+591">+591 BOL</option>
            <option value="+387">+387 BIH</option>
            <option value="+267">+267 BWA</option>
            <option value="+55">+55 BRA</option>
            <option value="+673">+673 BRN</option>
            <option value="+359">+359 BGR</option>
            <option value="+226">+226 BFA</option>
            <option value="+257">+257 BDI</option>
            <option value="+855">+855 KHM</option>
            <option value="+237">+237 CMR</option>
            <option value="+1">+1 CAN</option>
            <option value="+238">+238 CPV</option>
            <option value="+1-345">+1-345 CYM</option>
            <option value="+236">+236 CAF</option>
            <option value="+235">+235 TCD</option>
            <option value="+56">+56 CHL</option>
            <option value="+86">+86 CHN</option>
            <option value="+57">+57 COL</option>
            <option value="+269">+269 COM</option>
            <option value="+242">+242 COG</option>
            <option value="+243">+243 COD</option>
            <option value="+682">+682 COK</option>
            <option value="+506">+506 CRI</option>
            <option value="+385">+385 HRV</option>
            <option value="+53">+53 CUB</option>
            <option value="+599">+599 CUW</option>
            <option value="+357">+357 CYP</option>
            <option value="+420">+420 CZE</option>
            <option value="+45">+45 DNK</option>
            <option value="+253">+253 DJI</option>
            <option value="+1-767">+1-767 DMA</option>
            <option value="+1-809">+1-809 DOM</option>
            <option value="+593">+593 ECU</option>
            <option value="+20">+20 EGY</option>
            <option value="+503">+503 SLV</option>
            <option value="+240">+240 GNQ</option>
            <option value="+291">+291 ERI</option>
            <option value="+372">+372 EST</option>
            <option value="+251">+251 ETH</option>
            <option value="+298">+298 FRO</option>
            <option value="+679">+679 FJI</option>
            <option value="+358">+358 FIN</option>
            <option value="+33">+33 FRA</option>
            <option value="+594">+594 GUF</option>
            <option value="+689">+689 PYF</option>
            <option value="+241">+241 GAB</option>
            <option value="+220">+220 GMB</option>
            <option value="+995">+995 GEO</option>
            <option value="+49">+49 DEU</option>
            <option value="+233">+233 GHA</option>
            <option value="+350">+350 GIB</option>
            <option value="+30">+30 GRC</option>
            <option value="+299">+299 GRL</option>
            <option value="+1-473">+1-473 GRD</option>
            <option value="+502">+502 GTM</option>
            <option value="+44">+44 GBR</option>
            <option value="+224">+224 GIN</option>
            <option value="+245">+245 GNB</option>
            <option value="+592">+592 GUY</option>
            <option value="+509">+509 HTI</option>
            <option value="+504">+504 HND</option>
            <option value="+36">+36 HUN</option>
            <option value="+354">+354 ISL</option>
            <option value="+91">+91 IND</option>
            <option value="+62">+62 IDN</option>
            <option value="+98">+98 IRN</option>
            <option value="+964">+964 IRQ</option>
            <option value="+353">+353 IRL</option>
            <option value="+972">+972 ISR</option>
            <option value="+39">+39 ITA</option>
            <option value="+225">+225 CIV</option>
            <option value="+1-876">+1-876 JAM</option>
            <option value="+81">+81 JPN</option>
            <option value="+962">+962 JOR</option>
            <option value="+7">+7 KAZ</option>
            <option value="+254">+254 KEN</option>
            <option value="+686">+686 KIR</option>
            <option value="+965">+965 KWT</option>
            <option value="+996">+996 KGZ</option>
            <option value="+856">+856 LAO</option>
            <option value="+371">+371 LVA</option>
            <option value="+961">+961 LBN</option>
            <option value="+266">+266 LSO</option>
            <option value="+231">+231 LBR</option>
            <option value="+218">+218 LBY</option>
            <option value="+423">+423 LIE</option>
            <option value="+370">+370 LTU</option>
            <option value="+352">+352 LUX</option>
            <option value="+261">+261 MDG</option>
            <option value="+265">+265 MWI</option>
            <option value="+60">+60 MYS</option>
            <option value="+960">+960 MDV</option>
            <option value="+223">+223 MLI</option>
            <option value="+356">+356 MLT</option>
            <option value="+692">+692 MHL</option>
            <option value="+596">+596 MTQ</option>
            <option value="+222">+222 MRT</option>
            <option value="+230">+230 MUS</option>
            <option value="+262">+262 REU</option>
            <option value="+52">+52 MEX</option>
            <option value="+691">+691 FSM</option>
            <option value="+373">+373 MDA</option>
            <option value="+377">+377 MCO</option>
            <option value="+258">+258 MOZ</option>
            <option value="+95">+95 MMR</option>
            <option value="+264">+264 NAM</option>
            <option value="+674">+674 NRU</option>
            <option value="+977">+977 NPL</option>
            <option value="+31">+31 NLD</option>
            <option value="+687">+687 NCL</option>
            <option value="+64">+64 NZL</option>
            <option value="+505">+505 NIC</option>
            <option value="+227">+227 NER</option>
            <option value="+234">+234 NGA</option>
            <option value="+683">+683 NIU</option>
            <option value="+672">+672 NFK</option>
            <option value="+850">+850 PRK</option>
            <option value="+82">+82 KOR</option>
            <option value="+965">+965 KWT</option>
            <option value="+853">+853 MAC</option>
            <option value="+389">+389 MKD</option>
            <option value="+261">+261 MDG</option>
            <option value="+52">+52 MEX</option>
            <option value="+970">+970 PSE</option>
            <option value="+507">+507 PAN</option>
            <option value="+675">+675 PNG</option>
            <option value="+595">+595 PRY</option>
            <option value="+51">+51 PER</option>
            <option value="+63">+63 PHL</option>
            <option value="+48">+48 POL</option>
            <option value="+351">+351 PRT</option>
            <option value="+1-787">+1-787 PRI</option>
            <option value="+974">+974 QAT</option>
            <option value="+40">+40 ROU</option>
            <option value="+250">+250 RWA</option>
            <option value="+685">+685 WSM</option>
            <option value="+378">+378 SMR</option>
            <option value="+966">+966 SAU</option>
            <option value="+221">+221 SEN</option>
            <option value="+381">+381 SRB</option>
            <option value="+248">+248 SCP</option>
            <option value="+232">+232 SLE</option>
            <option value="+65">+65 SGP</option>
            <option value="+421">+421 SVK</option>
            <option value="+386">+386 SVN</option>
            <option value="+677">+677 SLB</option>
            <option value="+27">+27 ZAF</option>
            <option value="+500">+500 SGS</option>
            <option value="+34">+34 ESP</option>
            <option value="+94">+94 LKA</option>
            <option value="+249">+249 SDN</option>
            <option value="+597">+597 SUR</option>
            <option value="+268">+268 SWZ</option>
            <option value="+46">+46 SWE</option>
            <option value="+41">+41 CHE</option>
            <option value="+963">+963 SYR</option>
            <option value="+886">+886 TWN</option>
            <option value="+992">+992 TJK</option>
            <option value="+255">+255 TZA</option>
            <option value="+66">+66 THA</option>
            <option value="+228">+228 TGO</option>
            <option value="+690">+690 TKL</option>
            <option value="+676">+676 TON</option>
            <option value="+216">+216 TUN</option>
            <option value="+90">+90 TUR</option>
            <option value="+993">+993 TKM</option>
            <option value="+256">+256 UGA</option>
            <option value="+380">+380 UKR</option>
            <option value="+971">+971 ARE</option>
            <option value="+44-1481">+44-1481 GGY</option>
            <option value="+598">+598 URY</option>
            <option value="+1-340">+1-340 VIR</option>
            <option value="+998">+998 UZB</option>
            <option value="+58">+58 VEN</option>
            <option value="+84">+84 VNM</option>
            <option value="+1284">+1-284 VGB</option>
            <option value="+1345">+1-345 TCA</option>
            <option value="+681">+681 WLF</option>
            <option value="+967">+967 YEM</option>
            <option value="+260">+260 ZMB</option>
            <option value="+263">+263 ZWE</option>
          </select>
 
          <input
            type="tel"
            name="contact"
            placeholder="Contact Number"
            value={formData.contact}
            onChange={handleChange}
            maxLength={10}
            required
            className="contact-field"
          />
        </div>
 
        {/* Password */}
        <div className="full-field full-password-field">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            minLength={6}
            required
          />
          <span
            className="toggle-password"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? "👁️‍🗨️" : "👁"}
          </span>
        </div>
 
        {/* Confirm Password */}
        <div className="full-field full-password-field">
          <input
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            placeholder="Re-enter Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            minLength={6}
            required
          />
          <span
            className="toggle-password"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? "👁️‍🗨️" : "👁"}
          </span>
        </div>
 
        {/* {error && (
          <p style={{ color: "red", fontSize: "14px", marginLeft: "-100px" }}>
            {error}
          </p>
        )} */}
 
        <button
          className="full-btn"
          type="submit"
          style={{
            cursor: otpVerified ? "pointer" : "not-allowed",
            opacity: otpVerified ? 1 : 0.6,
            pointerEvents: "auto",
          }}
        >
          Create Account
        </button>
 
 
      </form>
      <ErrorModal
        show={showErrorModal}
        message={error}
        onClose={() => {
          setShowErrorModal(false);
          setError("");
        }}
      />
 
    </div>
  );
}
 
export default FullSignup;